import React, { useState, useEffect, useCallback, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import api from '../api';
import { useAuth } from '../AuthContext';
import NavBar from '../components/NavBar';
import LiveStatus from '../components/LiveStatus';
import StreamKey from '../components/StreamKey';
import HLSPlayer from '../components/HLSPlayer';
import DestinationModal from '../components/DestinationModal';
import { platformMeta } from '../platforms';

function formatDuration(secs) {
  if (!secs) return '—';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}h ${m}m`;
  if (m > 0) return `${m}m ${s}s`;
  return `${s}s`;
}

function formatUptime(secs) {
  if (!secs) return '0:00';
  const h = Math.floor(secs / 3600);
  const m = Math.floor((secs % 3600) / 60);
  const s = secs % 60;
  if (h > 0) return `${h}:${String(m).padStart(2,'0')}:${String(s).padStart(2,'0')}`;
  return `${m}:${String(s).padStart(2,'0')}`;
}

const statusColor = (st) => ({
  live:       { bg: 'rgba(0,230,118,0.1)',  border: 'rgba(0,230,118,0.25)',  text: '#00e676' },
  connecting: { bg: 'rgba(245,158,11,0.1)', border: 'rgba(245,158,11,0.25)', text: '#f59e0b' },
  error:      { bg: 'rgba(255,77,77,0.1)',  border: 'rgba(255,77,77,0.25)',  text: '#ff4d4d' },
  ended:      { bg: 'rgba(92,107,130,0.1)', border: 'var(--border)',          text: 'var(--text-muted)' },
}[st] || { bg: 'var(--surface2)', border: 'var(--border)', text: 'var(--text-muted)' });

export default function Dashboard() {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [streamInfo, setStreamInfo]       = useState(null);
  const [globalDests, setGlobalDests]     = useState([]);
  const [personalDests, setPersonalDests] = useState([]);
  const [status, setStatus]               = useState(null);
  const [health, setHealth]               = useState(null);
  const [showViewers, setShowViewers]     = useState(false);
  const [healthHistory, setHealthHistory] = useState([]);
  const [viewerGeo, setViewerGeo]         = useState({});
  const [history, setHistory]             = useState([]);
  const [modal, setModal]                 = useState(null);
  const [activeTab, setActiveTab]         = useState('destinations');
  const [uptime, setUptime]               = useState(0);
  const [handoff, setHandoff]             = useState(null);
  const [handoffSettings, setHandoffSettings] = useState({warningSeconds:30, crossfade:false});

  // Pre-stream settings (set before going live)
  const [preStream, setPreStream] = useState({
    title: '',
    recordingEnabled: null, // null = use server default
  });
  const [preStreamSaved, setPreStreamSaved] = useState(false);

  const canManageGlobal   = ['admin', 'manager'].includes(user?.role);
  const canCreatePersonal = user?.can_create_destinations !== false;
  const isAdmin           = user?.role === 'admin';
  const isManager         = ['admin', 'manager'].includes(user?.role);
  const wasLive           = useRef(false);

  const loadStreamKey     = useCallback(async () => { try { const {data} = await api.get('/stream/key'); setStreamInfo(data); } catch {} }, []);
  const loadGlobalDests   = useCallback(async () => { try { const {data} = await api.get('/stream/global-destinations'); setGlobalDests(data); } catch {} }, []);
  const loadPersonalDests = useCallback(async () => { try { const {data} = await api.get('/stream/destinations'); setPersonalDests(data); } catch {} }, []);
  const loadStatus        = useCallback(async () => { try { const {data} = await api.get('/stream/status'); setStatus(data); } catch {} }, []);
  const loadHealth        = useCallback(async () => {
    try {
      const {data} = await api.get('/stream-health/current');
      setHealth(data);
      if (data.live) {
        const {data: hist} = await api.get('/stream-health/history').catch(()=>({data:[]}));
        setHealthHistory(hist || []);
      }
    } catch {}
  }, []);
  const loadHandoff = useCallback(async()=>{
    if(!isManager) return;
    try{ const {data}=await api.get('/handoff/status'); setHandoff(data); }catch{}
  },[isManager]);
  const loadHistory       = useCallback(async () => { try { const {data} = await api.get('/stream/history'); setHistory(data); } catch {} }, []);

  // Load server default for recording
  useEffect(() => {
    api.get('/settings').then(({ data }) => {
      setPreStream(p => ({
        ...p,
        recordingEnabled: data.recording_enabled_default === 'true',
      }));
    }).catch(() => {});
  }, []);

  useEffect(() => {
    loadStreamKey(); loadGlobalDests(); loadPersonalDests(); loadStatus(); loadHistory();
    const statusInterval = setInterval(loadStatus, 5000);
    const healthInterval = setInterval(loadHealth, 3000);
    if(isManager){ loadHandoff(); const hi=setInterval(loadHandoff,5000); return()=>{clearInterval(statusInterval);clearInterval(healthInterval);clearInterval(hi);} }
    return () => { clearInterval(statusInterval); clearInterval(healthInterval); };
  }, [loadStreamKey, loadGlobalDests, loadPersonalDests, loadStatus, loadHistory, loadHealth]);

  // Uptime counter — use uptimeSeconds from server, only reset when stream changes
  const uptimeInitialized = useRef(false);
  const lastSessionId = useRef(null);
  useEffect(() => {
    if (!status?.live) { 
      setUptime(0); 
      uptimeInitialized.current = false;
      lastSessionId.current = null;
      return; 
    }
    // New session or first load — sync from server
    const sessionChanged = status.sessionId !== lastSessionId.current;
    if (!uptimeInitialized.current || sessionChanged) {
      setUptime(status.uptimeSeconds || 0);
      uptimeInitialized.current = true;
      lastSessionId.current = status.sessionId;
    }
    const t = setInterval(() => setUptime(u => u + 1), 1000);
    return () => clearInterval(t);
  }, [status?.live, status?.sessionId]);

  // Save pre-stream settings to stream key so SRS hook can read them
  const savePreStream = async () => {
    try {
      await api.post('/stream/pre-stream', preStream);
      setPreStreamSaved(true);
      setTimeout(() => setPreStreamSaved(false), 2000);
    } catch (err) {
      console.error('Failed to save pre-stream settings:', err);
    }
  };

  const toggleGlobal   = async (dest) => { await api.patch(`/stream/global-destinations/${dest.id}`, { enabled: !dest.enabled }); loadGlobalDests(); };
  const retryDest = async (dest, isGlobal) => {
    const route = isGlobal ? `/stream/global-destinations/${dest.id}` : `/stream/destinations/${dest.id}`;
    await api.patch(route, { enabled: false });
    await new Promise(r => setTimeout(r, 500));
    await api.patch(route, { enabled: true });
    if (isGlobal) loadGlobalDests(); else loadPersonalDests();
  };
  const deleteGlobal   = async (dest) => { if (!window.confirm(`Remove "${dest.label}"?`)) return; await api.delete(`/stream/global-destinations/${dest.id}`); loadGlobalDests(); };
  const togglePersonal = async (dest) => { await api.patch(`/stream/destinations/${dest.id}`, { enabled: !dest.enabled }); loadPersonalDests(); };
  const deletePersonal = async (dest) => { if (!window.confirm(`Remove "${dest.label}"?`)) return; await api.delete(`/stream/destinations/${dest.id}`); loadPersonalDests(); };
  const onModalSave    = () => { setModal(null); loadGlobalDests(); loadPersonalDests(); };

  const getLiveStatus = (id, isGlobal) => {
    if (!status?.live) return null;
    return status.destinations?.find(d => isGlobal ? d.global_destination_id === id : d.destination_id === id);
  };

  // Current stream health data
  // Prefer 'active' relay stream, then individual DJ stream
  const streamData = health?.streams?.find(s => s.name === 'active') ||
    health?.streams?.find(s => streamInfo?.streamKey && s.name === streamInfo.streamKey) ||
    health?.streams?.find(s => s.name && !s.name.includes('_720p') && !s.name.includes('_480p') &&
      !s.name.includes('.m3u8') && s.name !== 'fallback' && (s.width || s.bitrateKbps > 10)) ||
    null;
  const bitrateKbps = streamData?.bitrateKbps || 0;
  const fps = streamData?.fps || 0;
  // Use nginx viewer count (more accurate) - fetched separately
  const [nginxViewerCount, setNginxViewerCount] = useState(0);
  useEffect(() => {
    if (!status?.live) { setNginxViewerCount(0); return; }
    const fetchViewers = () => {
      api.get('/viewers/live').then(({data}) => {
        setNginxViewerCount(data.total || 0);
      }).catch(()=>{});
    };
    fetchViewers();
    const t = setInterval(fetchViewers, 30000);
    return () => clearInterval(t);
  }, [status?.live]);
  const viewerCount = nginxViewerCount;
  const resolution = streamData?.width && streamData?.height ? `${streamData.width}×${streamData.height}` : null;
  const isRecording = status?.live && health?.streams?.length > 0;

  const s = {
    page: { minHeight: '100vh', background: 'var(--bg)' },
    main: { maxWidth: '960px', margin: '0 auto', padding: '32px 24px' },

    // Pre-stream panel
    prePanel: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '20px 24px', marginBottom: '16px' },
    prePanelTitle: { fontSize: '13px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '16px' },
    preRow: { display: 'flex', gap: '14px', alignItems: 'flex-end', flexWrap: 'wrap' },
    preField: { flex: 2, minWidth: '200px' },
    preLabel: { display: 'block', fontSize: '11px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.7px', marginBottom: '6px' },
    preInput: { width: '100%', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '8px', padding: '9px 12px', color: 'var(--text)', fontSize: '13px', outline: 'none' },
    recToggle: { display: 'flex', alignItems: 'center', gap: '10px', padding: '9px 14px', background: 'var(--surface2)', border: '1px solid var(--border)', borderRadius: '8px', cursor: 'pointer', userSelect: 'none', flexShrink: 0 },
    recDot: (on) => ({ width: '8px', height: '8px', borderRadius: '50%', background: on ? '#ff4d4d' : 'var(--text-muted)', boxShadow: on ? '0 0 6px #ff4d4d' : 'none', flexShrink: 0 }),
    recLabel: (on) => ({ fontSize: '13px', fontWeight: '600', color: on ? 'var(--text)' : 'var(--text-muted)' }),
    savePreBtn: { padding: '9px 18px', background: 'var(--signal)', color: '#000', fontWeight: '700', fontSize: '13px', borderRadius: '8px', border: 'none', cursor: 'pointer', flexShrink: 0 },
    savedBadge: { padding: '9px 14px', background: 'rgba(0,230,118,0.1)', color: 'var(--signal)', borderRadius: '8px', fontSize: '12px', fontWeight: '600', border: '1px solid rgba(0,230,118,0.2)', flexShrink: 0 },

    // Live health panel
    healthPanel: { background: 'var(--surface)', border: '1px solid rgba(0,230,118,0.2)', borderRadius: 'var(--radius-lg)', padding: '16px 20px', marginBottom: '16px' },
    healthHeader: { display: 'flex', alignItems: 'center', gap: '10px', marginBottom: '14px' },
    liveDot: { width: '9px', height: '9px', borderRadius: '50%', background: 'var(--signal)', boxShadow: '0 0 8px var(--signal)', animation: 'pulse-dot 1.4s infinite', flexShrink: 0 },
    healthTitle: { fontWeight: '700', fontSize: '14px', flex: 1 },
    uptimeDisplay: { fontFamily: 'var(--mono)', fontSize: '16px', fontWeight: '700', color: 'var(--signal)' },
    healthGrid: { display: 'grid', gridTemplateColumns: 'repeat(5, 1fr)', gap: '10px' }, // ms-health-grid
    healthStat: { background: 'var(--surface2)', borderRadius: '8px', padding: '10px 12px', textAlign: 'center' },
    healthStatLabel: { fontSize: '10px', fontWeight: '700', color: 'var(--text-muted)', textTransform: 'uppercase', letterSpacing: '0.6px', marginBottom: '4px' },
    healthStatValue: (color) => ({ fontSize: '18px', fontWeight: '800', color: color || 'var(--text)', letterSpacing: '-0.5px' }),
    healthStatSub: { fontSize: '11px', color: 'var(--text-muted)', marginTop: '1px' },
    recIndicator: { display: 'flex', alignItems: 'center', gap: '6px', padding: '4px 10px', background: 'rgba(255,77,77,0.1)', borderRadius: '20px', border: '1px solid rgba(255,77,77,0.25)', fontSize: '11px', fontWeight: '700', color: '#ff4d4d' },
    recDotLive: { width: '7px', height: '7px', borderRadius: '50%', background: '#ff4d4d', animation: 'pulse-dot 1s infinite' },

    // Tabs
    tabs: { display: 'flex', gap: '2px', marginBottom: '24px', background: 'var(--surface)', padding: '4px', borderRadius: '10px', border: '1px solid var(--border)', width: 'fit-content' },
    tab: (active) => ({ padding: '7px 18px', borderRadius: '7px', fontSize: '13px', fontWeight: '600', cursor: 'pointer', background: active ? 'var(--surface2)' : 'transparent', color: active ? 'var(--text)' : 'var(--text-muted)', border: 'none' }),

    // Destinations
    sectionHeader: { display: 'flex', alignItems: 'center', justifyContent: 'space-between', marginBottom: '12px' },
    sectionTitle: { fontSize: '15px', fontWeight: '700' },
    sectionSub: { fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' },
    addBtn: { background: 'var(--signal)', color: '#000', fontWeight: '700', fontSize: '12px', borderRadius: '7px', padding: '7px 14px', cursor: 'pointer', border: 'none' },
    destList: { display: 'flex', flexDirection: 'column', gap: '8px', marginBottom: '28px' },
    destCard: (enabled) => ({ background: 'var(--surface)', border: `1px solid ${enabled ? 'var(--border)' : 'rgba(92,107,130,0.2)'}`, borderRadius: '10px', padding: '13px 16px', display: 'flex', alignItems: 'center', gap: '12px', opacity: enabled ? 1 : 0.55 }),
    platDot: (color) => ({ width: '8px', height: '8px', borderRadius: '50%', background: color, flexShrink: 0 }),
    destInfo: { flex: 1, minWidth: 0 },
    destLabel: { fontWeight: '600', fontSize: '14px' },
    destSub: { fontSize: '11px', color: 'var(--text-muted)', fontFamily: 'var(--mono)', overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', marginTop: '2px' },
    livePill: (st) => { const c = statusColor(st); return { fontSize: '10px', fontWeight: '800', padding: '3px 8px', borderRadius: '20px', background: c.bg, color: c.text, border: `1px solid ${c.border}`, whiteSpace: 'nowrap' }; },
    toggleBadge: (on) => ({ fontSize: '11px', fontWeight: '700', padding: '3px 8px', borderRadius: '20px', background: on ? 'rgba(0,230,118,0.08)' : 'var(--surface2)', color: on ? 'var(--signal)' : 'var(--text-muted)', border: `1px solid ${on ? 'rgba(0,230,118,0.2)' : 'var(--border)'}`, cursor: 'pointer' }),
    editBtn: { background: 'var(--surface2)', border: '1px solid var(--border)', color: 'var(--text-dim)', borderRadius: '7px', padding: '5px 10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
    delBtn: { background: 'var(--danger-bg)', border: '1px solid rgba(255,77,77,0.2)', color: 'var(--danger)', borderRadius: '7px', padding: '5px 10px', fontSize: '12px', fontWeight: '600', cursor: 'pointer' },
    actions: { display: 'flex', gap: '6px' },
    empty: { textAlign: 'center', padding: '32px 24px', background: 'var(--surface)', border: '1px dashed var(--border)', borderRadius: '10px', color: 'var(--text-muted)', fontSize: '13px' },
    divider: { height: '1px', background: 'var(--border)', margin: '24px 0' },
    globalBadge: { fontSize: '10px', fontWeight: '800', padding: '2px 6px', borderRadius: '4px', background: 'rgba(0,230,118,0.1)', color: 'var(--signal)', border: '1px solid rgba(0,230,118,0.2)', marginLeft: '6px', verticalAlign: 'middle' },
    personalBadge: { fontSize: '10px', fontWeight: '800', padding: '2px 6px', borderRadius: '4px', background: 'rgba(145,70,255,0.1)', color: '#9146ff', border: '1px solid rgba(145,70,255,0.2)', marginLeft: '6px', verticalAlign: 'middle' },
    obsCard: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: 'var(--radius-lg)', padding: '22px 24px' },
    obsTh: { textAlign: 'left', padding: '8px 12px', background: 'var(--surface2)', color: 'var(--text-muted)', fontSize: '11px', fontWeight: '700', textTransform: 'uppercase', letterSpacing: '0.5px' },
    obsTd: { padding: '10px 12px', borderBottom: '1px solid var(--border)', verticalAlign: 'top' },
    obsVal: { fontFamily: 'var(--mono)', color: 'var(--signal)', fontSize: '12px', wordBreak: 'break-all' },
    histCard: { background: 'var(--surface)', border: '1px solid var(--border)', borderRadius: '10px', padding: '14px 18px', marginBottom: '8px' },
    histMeta: { display: 'flex', justifyContent: 'space-between', alignItems: 'center', marginBottom: '8px' },
    histDate: { fontSize: '13px', fontWeight: '600' },
    histDur: { fontSize: '12px', color: 'var(--text-muted)', fontFamily: 'var(--mono)' },
    histPills: { display: 'flex', gap: '6px', flexWrap: 'wrap' },
    histPill: (st) => { const c = st === 'ended' ? 'var(--signal)' : st === 'error' ? 'var(--danger)' : 'var(--text-muted)'; return { fontSize: '11px', padding: '2px 8px', borderRadius: '20px', background: `${c}15`, color: c, border: `1px solid ${c}30` }; },
  };

  const DestCard = ({ dest, isGlobal }) => {
    const pm = platformMeta(dest.platform);
    const ls = getLiveStatus(dest.id, isGlobal);
    const canEdit   = isGlobal ? canManageGlobal : true;
    const canDelete = isGlobal ? isAdmin : true;
    const canToggle = isGlobal ? canManageGlobal : true;
    return (
      <div style={s.destCard(dest.enabled)}>
        <div style={s.platDot(pm.color)} />
        <div style={s.destInfo}>
          <div style={s.destLabel}>
            {dest.label}
            {isGlobal && <span style={s.globalBadge}>GLOBAL</span>}
          </div>
          <div style={s.destSub}>{pm.label} · {dest.rtmp_url.replace(/^rtmps?:\/\//, '').split('/')[0]}</div>
        </div>
        {ls && (
          <span style={s.livePill(ls.status)}>
            {ls.status === 'live' ? '● LIVE' : ls.status === 'connecting' ? '◌ Connecting' : ls.status === 'error' ? '✕ Error' : '—'}
          </span>
        )}
        {canToggle && (
          <>
          <span style={s.toggleBadge(dest.enabled)} onClick={() => isGlobal ? toggleGlobal(dest) : togglePersonal(dest)}>
            {dest.enabled ? 'On' : 'Off'}
          </span>
          {dest.enabled && (() => {
            const ds = getLiveStatus(dest.id, isGlobal);
            if (ds?.status === 'live') return <span style={{fontSize:'10px',color:'var(--signal)',fontWeight:'700',marginLeft:'6px'}}>● LIVE</span>;
            if (ds?.status === 'connecting') return <span style={{fontSize:'10px',color:'#f59e0b',fontWeight:'700',marginLeft:'6px'}}>⟳ Connecting</span>;
            return <button style={{fontSize:'10px',padding:'2px 7px',marginLeft:'6px',background:'rgba(99,102,241,0.15)',border:'1px solid rgba(99,102,241,0.3)',color:'#818cf8',borderRadius:'4px',cursor:'pointer'}}
              onClick={e=>{e.stopPropagation();retryDest(dest,isGlobal);}}>↺ Retry</button>;
          })()}
          </>
        )}
        <div style={s.actions}>
          {canEdit && <button style={s.editBtn} onClick={() => setModal({ type: isGlobal ? 'global' : 'personal', dest })}>Edit</button>}
          {canDelete && <button style={s.delBtn} onClick={() => isGlobal ? deleteGlobal(dest) : deletePersonal(dest)}>Remove</button>}
        </div>
      </div>
    );
  };

  const bitrateColor = bitrateKbps > 4000 ? 'var(--signal)' : bitrateKbps > 1500 ? '#f59e0b' : bitrateKbps > 0 ? 'var(--danger)' : 'var(--text-muted)';

  return (
        <NavBar liveStatus={status}>
    <div style={s.page}>
      <style>{`
        @keyframes pulse-dot {
          0% { box-shadow: 0 0 0 0 rgba(0,230,118,0.6); }
          70% { box-shadow: 0 0 0 6px rgba(0,230,118,0); }
          100% { box-shadow: 0 0 0 0 rgba(0,230,118,0); }
        }
      `}</style>

      <div style={s.main} className="ms-content">
        <LiveStatus status={status} />

        {/* ── PRE-STREAM PANEL (shown when offline) ── */}
        {!status?.live && (
          <div style={s.prePanel}>
            <div style={s.prePanelTitle}>🎙 Pre-stream Setup</div>
            <div style={s.preRow}>
              <div style={s.preField}>
                <label style={s.preLabel}>Show Title (optional)</label>
                <input
                  style={s.preInput}
                  placeholder="e.g. Morning Show with Barny"
                  value={preStream.title}
                  onChange={e => setPreStream(p => ({ ...p, title: e.target.value }))}
                />
              </div>

              {preStreamSaved
                ? <div style={s.savedBadge}>✓ Saved</div>
                : <button style={s.savePreBtn} onClick={savePreStream}>Save</button>
              }
            </div>
            <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '10px' }}>
              Set a show title before going live — it appears on the public page and is saved with the recording.
              Toggle recording on/off using the switch in Connection Details below.
            </div>
          </div>
        )}

        {/* ── LIVE HEALTH PANEL (shown when live) ── */}
        {status?.live && (
          <div style={s.healthPanel}>
            <div style={s.healthHeader}>
              <div style={s.liveDot} />
              <div style={s.healthTitle}>
                {status.streamTitle || 'Live Now'}
              </div>
              <div style={s.uptimeDisplay}>{formatUptime(uptime)}</div>
              {status.recording_enabled && (
                <div style={s.recIndicator}>
                  <div style={s.recDotLive} />
                  REC
                </div>
              )}
            </div>
            <div style={s.healthGrid} className="ms-health-grid">
              <div style={s.healthStat}>
                <div style={s.healthStatLabel}>Bitrate</div>
                <div style={s.healthStatValue(bitrateColor)}>
                  {bitrateKbps > 0 ? `${bitrateKbps}` : '—'}
                </div>
                <div style={s.healthStatSub}>kbps</div>
              </div>
              <div style={s.healthStat}>
                <div style={s.healthStatLabel}>Frame Rate</div>
                <div style={s.healthStatValue(fps > 0 ? 'var(--text)' : 'var(--text-muted)')}>
                  {fps > 0 ? fps.toFixed(1) : '—'}
                </div>
                <div style={s.healthStatSub}>fps</div>
              </div>
              <div style={s.healthStat}>
                <div style={s.healthStatLabel}>Resolution</div>
                <div style={{ ...s.healthStatValue(), fontSize: resolution ? '14px' : '18px' }}>
                  {resolution || '—'}
                </div>
                <div style={s.healthStatSub}>video</div>
              </div>
              <div style={s.healthStat}>
                <div style={s.healthStatLabel}>Destinations</div>
                <div style={s.healthStatValue('var(--signal)')}>
                  {globalDests?.filter(d => d.enabled).length || 0}
                  <span style={{ fontSize: '13px', color: 'var(--text-muted)', fontWeight: '400' }}>
                    /{globalDests?.length || 0}
                  </span>
                </div>
                <div style={s.healthStatSub}>live</div>
              </div>
              <div style={{...s.healthStat, cursor: viewerCount > 0 ? 'pointer' : 'default'}}
                onClick={() => {
                  if (viewerCount === 0) return;
                  setShowViewers(true);
                  setViewerGeo({});
                  // Fetch live viewer data from nginx logs
                  fetch('/api/viewers/live', { headers: { Authorization: 'Bearer ' + localStorage.getItem('ms_token') } })
                    .then(r => r.json()).then(data => {
                      const viewers = data.viewers || [];
                      const geoMap = {};
                      viewers.forEach(v => { geoMap[v.ip] = v; });
                      // If no external viewers, show internal ones too
                      if (Object.keys(geoMap).length === 0) {
                        geoMap['no-data'] = { ip: 'No viewer data', quality: '—', client: '—', requests: 0 };
                      }
                      setViewerGeo(geoMap);
                    }).catch(()=>{ setViewerGeo({'error': {ip:'Failed to load', quality:'—', client:'—', requests:0}}); });
                }}>
                <div style={s.healthStatLabel}>Viewers {viewerCount > 0 && <span style={{fontSize:'9px',opacity:0.6}}>▼</span>}</div>
                <div style={s.healthStatValue(viewerCount > 0 ? 'var(--signal)' : 'var(--text-muted)')}>
                  {viewerCount > 0 ? viewerCount : '—'}
                </div>
                <div style={s.healthStatSub}>watching</div>
              </div>
            </div>
          </div>
        )}

        {/* ── STREAM HISTORY GRAPH ── */}
        {status?.live && healthHistory.length > 2 && (
          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'16px 20px',marginBottom:'16px'}}>
            <div style={{fontWeight:'700',fontSize:'13px',marginBottom:'12px',color:'var(--text-muted)',textTransform:'uppercase',letterSpacing:'0.5px'}}>
              📈 Bitrate History
            </div>
            <div style={{position:'relative',height:'60px',width:'100%'}}>
              <svg width="100%" height="60" preserveAspectRatio="none" style={{display:'block'}}>
                {(() => {
                  const pts = healthHistory.filter(h => h.bitrate_kbps > 0);
                  if (pts.length < 2) return null;
                  const maxBr = Math.max(...pts.map(p => p.bitrate_kbps));
                  const minBr = Math.min(...pts.map(p => p.bitrate_kbps));
                  const range = maxBr - minBr || 1;
                  const w = 100, h = 60, pad = 4;
                  const x = (i) => (i / (pts.length - 1)) * w;
                  const y = (v) => h - pad - ((v - minBr) / range) * (h - pad * 2);
                  const path = pts.map((p, i) => `${i===0?'M':'L'}${x(i).toFixed(1)},${y(p.bitrate_kbps).toFixed(1)}`).join(' ');
                  const area = path + ` L${w},${h} L0,${h} Z`;
                  return (
                    <>
                      <defs>
                        <linearGradient id="brGrad" x1="0" y1="0" x2="0" y2="1">
                          <stop offset="0%" stopColor="var(--signal)" stopOpacity="0.3"/>
                          <stop offset="100%" stopColor="var(--signal)" stopOpacity="0.02"/>
                        </linearGradient>
                      </defs>
                      <path d={area} fill="url(#brGrad)" vectorEffect="non-scaling-stroke"/>
                      <path d={path} fill="none" stroke="var(--signal)" strokeWidth="1.5" vectorEffect="non-scaling-stroke"/>
                    </>
                  );
                })()}
              </svg>
              <div style={{display:'flex',justifyContent:'space-between',fontSize:'10px',color:'var(--text-muted)',marginTop:'4px'}}>
                <span>{healthHistory[0] ? new Date(healthHistory[0].timestamp).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : ''}</span>
                <span style={{color:'var(--signal)',fontWeight:'700'}}>{bitrateKbps} kbps now</span>
                <span>{healthHistory[healthHistory.length-1] ? new Date(healthHistory[healthHistory.length-1].timestamp).toLocaleTimeString([],{hour:'2-digit',minute:'2-digit'}) : ''}</span>
              </div>
            </div>
          </div>
        )}

        {/* ── HANDOFF PANEL (Admin/Manager only) ── */}
        {isManager && handoff && handoff.connectedDJs?.length > 0 && (
          <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'var(--radius-lg)',padding:'16px 20px',marginBottom:'16px'}}>
            <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'14px'}}>
              <div style={{fontWeight:'700',fontSize:'14px'}}>🎚 DJ Mixer</div>
              <div style={{display:'flex',gap:'8px',alignItems:'center'}}>
                <label style={{fontSize:'11px',color:'var(--text-muted)',display:'flex',alignItems:'center',gap:'6px',cursor:'pointer'}}>
                  <input type="checkbox" checked={handoffSettings.crossfade} onChange={e=>setHandoffSettings(s=>({...s,crossfade:e.target.checked}))}/>
                  Crossfade
                </label>
                <select value={handoffSettings.warningSeconds} onChange={e=>setHandoffSettings(s=>({...s,warningSeconds:parseInt(e.target.value)}))}
                  style={{fontSize:'11px',background:'var(--surface2)',border:'1px solid var(--border)',borderRadius:'5px',color:'var(--text)',padding:'3px 6px'}}>
                  <option value={0}>Instant</option>
                  <option value={15}>15s warning</option>
                  <option value={30}>30s warning</option>
                  <option value={60}>60s warning</option>
                </select>
              </div>
            </div>

            {/* Pending handoff warning */}
            {handoff.pendingHandoff && (
              <div style={{background:'rgba(245,158,11,0.1)',border:'1px solid rgba(245,158,11,0.3)',borderRadius:'8px',padding:'10px 14px',marginBottom:'12px',display:'flex',alignItems:'center',justifyContent:'space-between'}}>
                <div style={{fontSize:'13px',color:'#f59e0b',fontWeight:'600'}}>
                  ⏳ Handoff in {Math.max(0,Math.round((handoff.pendingHandoff.switchAt - Date.now())/1000))}s…
                </div>
                <button style={{fontSize:'11px',padding:'4px 10px',background:'var(--danger-bg)',border:'1px solid rgba(255,77,77,0.3)',color:'var(--danger)',borderRadius:'6px',cursor:'pointer'}}
                  onClick={async()=>{ await api.post('/handoff/cancel'); loadHandoff(); }}>Cancel</button>
              </div>
            )}

            {/* Connected DJs - side by side */}
            <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'10px'}} className="ms-mixer-grid">
              {[...handoff.connectedDJs].sort((a,b) => (b.isActive ? 1 : 0) - (a.isActive ? 1 : 0)).map(dj=>(
                <div key={dj.streamKey} style={{
                  borderRadius:'10px',overflow:'hidden',marginBottom:'6px',
                  background:dj.isActive?'rgba(232,92,53,0.06)':'var(--surface2)',
                  border:`1px solid ${dj.isActive?'rgba(232,92,53,0.35)':'var(--border)'}`,
                }}>
                  {/* Mini preview strip - 16:9 aspect ratio */}
                  <div style={{position:'relative',paddingBottom:'56.25%',background:'#1a0d08',overflow:'hidden'}}>
                    <video autoPlay muted playsInline style={{position:'absolute',top:0,left:0,width:'100%',height:'100%',objectFit:'cover',opacity:0.9}}
                      ref={el=>{
                        if(!el||el._loaded) return;
                        el._loaded=true;
                        const url=dj.isActive
                          ?'https://dfmtv.arnait.com/live/1080p.m3u8'
                          :`https://dfmtv.arnait.com/hls/${dj.streamKey}.m3u8`;
                        const init=()=>{
                          if(window.Hls&&window.Hls.isSupported()){
                            const h=new window.Hls({liveSyncDurationCount:2,lowLatencyMode:true});
                            h.loadSource(url); h.attachMedia(el);
                            h.on(window.Hls.Events.MANIFEST_PARSED,()=>el.play().catch(()=>{}));
                            el._hls=h;
                          } else if(el.canPlayType('application/vnd.apple.mpegurl')){
                            el.src=url; el.play().catch(()=>{});
                          }
                        };
                        if(window.Hls) init();
                        else {
                          const s=document.createElement('script');
                          s.src='https://cdnjs.cloudflare.com/ajax/libs/hls.js/1.5.7/hls.min.js';
                          s.onload=init; document.head.appendChild(s);
                        }
                      }}
                    />
                    <div style={{position:'absolute',top:'5px',left:'7px',fontSize:'9px',fontWeight:'800',
                      color:'white',background:dj.isActive?'var(--signal)':'rgba(0,0,0,0.55)',
                      padding:'2px 7px',borderRadius:'4px',letterSpacing:'0.04em'}}>
                      {dj.isActive?'● LIVE':'◎ PREVIEW'}
                    </div>
                  </div>
                  {/* DJ info row */}
                  <div style={{display:'flex',alignItems:'center',gap:'10px',padding:'8px 12px'}}>
                    <div style={{width:'7px',height:'7px',borderRadius:'50%',flexShrink:0,
                      background:dj.isActive?'var(--signal)':'var(--text-muted)',
                      boxShadow:dj.isActive?'0 0 6px var(--signal)':'none'}}/>
                    <div style={{flex:1,minWidth:0}}>
                      <div style={{fontWeight:'600',fontSize:'13px',whiteSpace:'nowrap',overflow:'hidden',textOverflow:'ellipsis'}}>{dj.name}</div>
                      <div style={{fontSize:'11px',color:'var(--text-muted)'}}>{dj.role}</div>
                    </div>
                    {dj.isActive?(
                      <span style={{fontSize:'10px',fontWeight:'800',padding:'3px 8px',borderRadius:'20px',
                        background:'rgba(232,92,53,0.12)',color:'var(--signal)',
                        border:'1px solid rgba(232,92,53,0.3)',whiteSpace:'nowrap'}}>● ON AIR</span>
                    ):(
                      <button style={{fontSize:'11px',fontWeight:'700',padding:'5px 12px',borderRadius:'7px',
                        background:'var(--signal)',color:'#fff',border:'none',cursor:'pointer',whiteSpace:'nowrap'}}
                        onClick={async()=>{
                          try {
                            await api.post('/handoff/switch',{
                              fromKey:handoff.activeKey,
                              toKey:dj.streamKey,
                              warningSeconds:handoffSettings.warningSeconds,
                              crossfade:handoffSettings.crossfade
                            });
                            loadHandoff();
                          } catch(e){ console.error('Handoff error:', e); }
                        }}>
                        {handoffSettings.warningSeconds===0?'Go Live':'Queue'}
                      </button>
                    )}
                  </div>
                </div>
              ))}
            </div>

            {handoff.relayActive && (
              <button style={{marginTop:'10px',fontSize:'11px',padding:'5px 12px',background:'var(--danger-bg)',border:'1px solid rgba(255,77,77,0.2)',color:'var(--danger)',borderRadius:'6px',cursor:'pointer'}}
                onClick={async()=>{ await api.post('/handoff/stop'); loadHandoff(); }}>
                Stop Relay (go to fallback)
              </button>
            )}
          </div>
        )}

        <HLSPlayer hlsUrl={status?.live ? 'https://dfmtv.arnait.com/live/master.m3u8' : (status?.hlsUrl || streamInfo?.hlsUrl)} live={status?.live} streamKey={streamInfo?.streamKey} serverHost={window.location.host} />
        <StreamKey streamInfo={streamInfo} onRegenerate={loadStreamKey} />

        <div style={s.tabs}>
          {['destinations', 'setup-guide', 'history'].map(tab => (
            <button key={tab} style={s.tab(activeTab === tab)} onClick={() => setActiveTab(tab)}>
              {tab === 'destinations'
                ? `Destinations (${globalDests.length + personalDests.length})`
                : tab === 'setup-guide' ? 'Setup Guide' : 'History'}
            </button>
          ))}
        </div>

        {/* DESTINATIONS TAB */}
        {activeTab === 'destinations' && (
          <div>
            <div style={s.sectionHeader}>
              <div>
                <div style={s.sectionTitle}>Global Destinations <span style={s.globalBadge}>SHARED</span></div>
                <div style={s.sectionSub}>All users stream to these simultaneously</div>
              </div>
              {canManageGlobal && (
                <button style={s.addBtn} onClick={() => setModal({ type: 'global', dest: null })}>+ Add global</button>
              )}
            </div>
            {globalDests.length === 0 ? (
              <div style={s.empty}>{canManageGlobal ? 'No global destinations yet' : 'No global destinations — contact your admin'}</div>
            ) : (
              <div style={s.destList}>{globalDests.map(dest => <DestCard key={dest.id} dest={dest} isGlobal={true} />)}</div>
            )}

            <div style={s.divider} />

            <div style={s.sectionHeader}>
              <div>
                <div style={s.sectionTitle}>My Destinations <span style={s.personalBadge}>PERSONAL</span></div>
                <div style={s.sectionSub}>Only used when you stream</div>
              </div>
              {canCreatePersonal && (
                <button style={s.addBtn} onClick={() => setModal({ type: 'personal', dest: null })}>+ Add personal</button>
              )}
            </div>
            {personalDests.length === 0 ? (
              <div style={s.empty}>{canCreatePersonal ? 'No personal destinations yet' : 'Personal destinations disabled for your account'}</div>
            ) : (
              <div style={s.destList}>{personalDests.map(dest => <DestCard key={dest.id} dest={dest} isGlobal={false} />)}</div>
            )}
          </div>
        )}

        {/* SETUP GUIDE TAB */}
        {activeTab === 'setup-guide' && streamInfo && (
          <div style={s.obsCard}>
            <table style={{ width: '100%', borderCollapse: 'collapse', fontSize: '13px' }}>
              <thead>
                <tr>
                  <th style={s.obsTh}>Protocol</th>
                  <th style={s.obsTh}>Use Case</th>
                  <th style={s.obsTh}>Server / URL</th>
                  <th style={s.obsTh}>Key</th>
                </tr>
              </thead>
              <tbody>
                <tr><td style={s.obsTd}><strong>RTMP</strong></td><td style={{...s.obsTd,color:'var(--text-muted)'}}>OBS, vMix, standard encoders</td><td style={s.obsTd}><span style={s.obsVal}>{streamInfo.ingestUrl}</span></td><td style={s.obsTd}><span style={s.obsVal}>your stream key</span></td></tr>
                <tr><td style={s.obsTd}><strong>RTMPS</strong></td><td style={{...s.obsTd,color:'var(--text-muted)'}}>Encrypted</td><td style={s.obsTd}><span style={s.obsVal}>{streamInfo.rtmpsUrl}</span></td><td style={s.obsTd}><span style={s.obsVal}>your stream key</span></td></tr>
                <tr><td style={s.obsTd}><strong>SRT</strong></td><td style={{...s.obsTd,color:'var(--text-muted)'}}>Low latency</td><td style={s.obsTd} colSpan={2}><span style={s.obsVal}>{streamInfo.srtUrl}</span></td></tr>
                <tr><td style={s.obsTd}><strong>HLS</strong></td><td style={{...s.obsTd,color:'var(--text-muted)'}}>Browser playback</td><td style={s.obsTd} colSpan={2}><span style={s.obsVal}>{streamInfo.hlsUrl}</span></td></tr>
              </tbody>
            </table>
          </div>
        )}

        {/* HISTORY TAB */}
        {activeTab === 'history' && (
          <div>
            {history.length === 0 ? (
              <div style={s.empty}>No stream history yet</div>
            ) : history.map(session => (
              <div key={session.id} style={s.histCard}>
                <div style={s.histMeta}>
                  <div>
                    <div style={s.histDate}>{new Date(session.started_at).toLocaleString()}</div>
                    {session.title && <div style={{ fontSize: '12px', color: 'var(--text-muted)', marginTop: '2px' }}>{session.title}</div>}
                  </div>
                  <div style={s.histDur}>{formatDuration(session.duration_secs)}</div>
                </div>
                <div style={s.histPills}>
                  {(session.destinations || []).map((d, i) => (
                    <div key={i} style={s.histPill(d.status)}>{d.label}</div>
                  ))}
                  {(session.destinations || []).length === 0 && (
                    <div style={s.histPill('ended')}>Preview only</div>
                  )}
                </div>
              </div>
            ))}
          </div>
        )}
      </div>

      {modal && (
        <DestinationModal
          destination={modal.dest}
          type={modal.type}
          onSave={onModalSave}
          onClose={() => setModal(null)}
        />
      )}
    </div>
    {/* Viewer Modal */}
    {showViewers && (
      <div style={{position:'fixed',inset:0,background:'rgba(0,0,0,0.6)',zIndex:1000,display:'flex',alignItems:'center',justifyContent:'center',padding:'20px'}}
        onClick={e => e.target===e.currentTarget && setShowViewers(false)}>
        <div style={{background:'var(--surface)',border:'1px solid var(--border)',borderRadius:'16px',padding:'24px',width:'100%',maxWidth:'600px',maxHeight:'80vh',overflow:'hidden',display:'flex',flexDirection:'column'}}>
          <div style={{display:'flex',alignItems:'center',justifyContent:'space-between',marginBottom:'16px'}}>
            <div style={{fontWeight:'800',fontSize:'16px'}}>👁 {viewerCount} Viewers</div>
            <button onClick={()=>setShowViewers(false)} style={{background:'none',border:'none',color:'var(--text-muted)',fontSize:'20px',cursor:'pointer'}}>✕</button>
          </div>
          <div style={{overflowY:'auto',flex:1}}>
            {Object.keys(viewerGeo).length === 0 ? (
              <div style={{color:'var(--text-muted)',fontSize:'13px',padding:'20px 0',textAlign:'center'}}>Loading viewer data…</div>
            ) : (
              <>
                <div style={{display:'grid',gridTemplateColumns:'1fr 1fr',gap:'8px',marginBottom:'12px'}}>
                  {['1080p','720p','480p','fallback'].map(q => {
                    const count = Object.values(viewerGeo).filter(v => v.quality === q).length;
                    return count > 0 ? (
                      <div key={q} style={{background:'var(--surface2)',borderRadius:'8px',padding:'10px',textAlign:'center'}}>
                        <div style={{fontSize:'18px',fontWeight:'800',color:'var(--signal)'}}>{count}</div>
                        <div style={{fontSize:'11px',color:'var(--text-muted)'}}>{q}</div>
                      </div>
                    ) : null;
                  })}
                </div>
                <table style={{width:'100%',borderCollapse:'collapse',fontSize:'12px'}}>
                  <thead>
                    <tr style={{borderBottom:'1px solid var(--border)'}}>
                      <th style={{textAlign:'left',padding:'6px 8px',color:'var(--text-muted)',fontWeight:'700',fontSize:'10px',textTransform:'uppercase'}}>IP</th>
                      <th style={{textAlign:'left',padding:'6px 8px',color:'var(--text-muted)',fontWeight:'700',fontSize:'10px',textTransform:'uppercase'}}>Quality</th>
                      <th style={{textAlign:'left',padding:'6px 8px',color:'var(--text-muted)',fontWeight:'700',fontSize:'10px',textTransform:'uppercase'}}>Client</th>
                      <th style={{textAlign:'right',padding:'6px 8px',color:'var(--text-muted)',fontWeight:'700',fontSize:'10px',textTransform:'uppercase'}}>Requests</th>
                    </tr>
                  </thead>
                  <tbody>
                    {Object.values(viewerGeo).sort((a,b) => b.requests - a.requests).map((v,i) => (
                      <tr key={i} style={{borderBottom:'1px solid var(--border)'}}>
                        <td style={{padding:'8px',fontFamily:'var(--mono)',fontSize:'11px'}}>{v.ip}</td>
                        <td style={{padding:'8px'}}>
                          <span style={{background:'var(--surface2)',color:'var(--text)',padding:'2px 6px',borderRadius:'4px',fontSize:'10px',fontWeight:'700',border:'1px solid var(--border)'}}>{v.quality}</span>
                        </td>
                        <td style={{padding:'8px',color:'var(--text-muted)'}}>{v.client}</td>
                        <td style={{padding:'8px',textAlign:'right',color:'var(--text-muted)'}}>{v.requests}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </>
            )}
          </div>
        </div>
      </div>
    )}
    </NavBar>
  );
}
